import _ from 'lodash'
import { useFormContext } from 'react-hook-form'

import { useGetViewports, ViewportSlice } from 'features/globalSetup/features/viewport'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'

import type { UseMediaTypeViewports, ViewportOptionSlice } from './types'
import type { SelectOption } from 'components/Select'
import type { EditSchema } from 'features/inventory/ad-unit/forms/GeneralForm/schemas/editSchema'
import type { MediaTypeViewportsSchema } from 'features/inventory/ad-unit/forms/GeneralForm/schemas/mediaTypesSchema'

export const useMediaTypeViewports = (): UseMediaTypeViewports => {
  const { workspaceId } = useWorkspaceParam()

  const { viewports } = useGetViewports({ workspaceId })

  const { watch } = useFormContext<EditSchema>()
  const {
    mediaTypes: { newViewportId, viewportAttributes },
  } = watch()

  const activeViewports: MediaTypeViewportsSchema['viewport'][] = viewportAttributes.map(
    ({ viewport }: MediaTypeViewportsSchema): MediaTypeViewportsSchema['viewport'] => viewport,
  )

  const inactiveViewports: ViewportSlice[] = _.differenceBy(
    viewports,
    activeViewports,
    'viewportId',
  )

  const options: SelectOption<ViewportOptionSlice>[] = inactiveViewports.map(
    ({ name, viewportId }: ViewportSlice): SelectOption<ViewportOptionSlice> => ({
      label: name,
      value: viewportId,
    }),
  )

  const getNewMediaTypeData: UseMediaTypeViewports['getNewMediaTypeData'] = () => {
    const template: undefined | ViewportSlice = inactiveViewports.find(
      (viewport: ViewportSlice): boolean => viewport.viewportId === newViewportId,
    )

    if (template === undefined) {
      return null
    }

    const schema: MediaTypeViewportsSchema = {
      ...template,
      viewport: template,
    }

    const position: number = _.sortedIndexBy(activeViewports, template, 'minimumWidth')

    return [position, schema]
  }

  return { getNewMediaTypeData, options }
}
